import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import StandardPagination from './StandardPagination';
import useImpersonation from '../hooks/useImpersonation';
import ImpersonationModal from './ImpersonationModal';
import styles from './AllStudents.module.css';

const AllStudents = ({ students, searchQuery, setSearchQuery, handleSearch, handlePageChange, currentPage, totalPages, itemsPerPage, onItemsPerPageChange, totalItems }) => {
  const navigate = useNavigate();
  const { startImpersonation, loading } = useImpersonation();
  const [impersonationModal, setImpersonationModal] = useState({ show: false, user: null });

  const handleImpersonateClick = (student) => {
    setImpersonationModal({ show: true, user: student });
  };

  const handleImpersonateConfirm = async () => {
    const { user } = impersonationModal;
    if (!user) return;

    const result = await startImpersonation(user._id, 'student');
    if (!result.success) {
      alert(result.error || 'Failed to start impersonation');
      setImpersonationModal({ show: false, user: null });
    }
  };

  const handleImpersonateCancel = () => {
    setImpersonationModal({ show: false, user: null });
  };

  return (
    <div className={styles.studentsTabContent}>
      <div className="container">
        <div>
          {/* Search Bar - At the Top */}
          <div className="d-flex justify-content-end mb-3">
            <Form onSubmit={handleSearch} className="form-inline">
              <Form.Control
                className="form-control mr-sm-2"
                type="search"
                name="search"
                placeholder="Student Name or Email"
                aria-label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Button className="btn btn-outline-success my-2 my-sm-0" type="submit">
                Search
              </Button>
            </Form>
          </div>
          
          {/* Items Per Page Controls - Below Search */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="d-flex align-items-center">
                <label htmlFor="studentsPerPage" className="form-label me-2 mb-0">Students per page:</label>
                <select 
                  id="studentsPerPage"
                  className="form-select"
                  value={itemsPerPage || 12}
                  onChange={(e) => onItemsPerPageChange && onItemsPerPageChange(parseInt(e.target.value))}
                  style={{ 
                    maxWidth: '80px', 
                    height: '32px',
                    fontSize: '14px',
                    padding: '4px 8px'
                  }}
                >
                  {[10, 20, 50, 100].map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-6 text-end">
              <small style={{ color: 'var(--darkOrange)' }}>
                Showing {((currentPage - 1) * (itemsPerPage || 12)) + 1} to {Math.min(currentPage * (itemsPerPage || 12), totalItems || students.length)} of {totalItems || students.length} students
              </small>
            </div>
          </div>
          <div className={styles.cardGrid}>
            {students.map(student => (
              <div key={student._id} className={styles.cardItem}>
                <div className={styles.card}>
                  <div className={styles.cardBody}>
                    <div className={styles.cardContent}>
                      <div className={styles.cardInfo}>
                        <h5 className={styles.cardTitle}>{student.firstName && student.lastName ? `${student.firstName} ${student.lastName}` : student.contactName}</h5>
                        <p className={styles.cardSubtitle} title={student.email}>Email: {student.email}</p>
                        <p className={styles.cardSubtitle} title={student.yearGrade || 'Not set'}>Year Group: {student.yearGrade || 'Not set'}</p>
                        <p className={styles.cardSubtitle} title={student.studentCode}>Student Code: {student.studentCode}</p>
                      </div>
                      <div className={styles.cardButton}>
                        <Button 
                          className={`btn ${styles.btnPrimary} ${styles.btnLeft}`}
                          onClick={() => navigate(`/student/${student._id}`)}
                        >
                          View<br/>Details
                        </Button>
                        <Button 
                          className={`btn ${styles.btnWarning} ${styles.btnRight}`}
                          onClick={() => handleImpersonateClick(student)}
                          title="Impersonate User"
                          disabled={loading}
                        >
                          Impersonate<br/>User
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <StandardPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage || 12}
            onItemsPerPageChange={onItemsPerPageChange}
            totalItems={totalItems || students.length}
            itemsPerPageLabel="Students per page:"
            pageInfoLabel="Showing"
            showItemsPerPage={false}
          />
          <br />
        </div>
      </div>

      <ImpersonationModal
        show={impersonationModal.show}
        onClose={handleImpersonateCancel}
        onConfirm={handleImpersonateConfirm}
        user={impersonationModal.user}
        loading={loading}
      />
    </div>
  );
};

export default AllStudents; 